// 공통 Role 정의 - 백엔드와 프론트엔드에서 공유
export enum UserRole {
  SUPER_ADMIN = 'SuperAdmin',
  ADMIN = 'Admin',
  COMPANY_MANAGER = 'CompanyManager',
  SITE_MANAGER = 'SiteManager',
  GUEST = 'Guest',
}

// role_id 상수 — DB roles 테이블과 1:1. 숫자가 작을수록 권한이 높다.
export const ROLE_ID = {
  SUPER_ADMIN: 0,
  ADMIN: 1,
  COMPANY_MANAGER: 2,
  SITE_MANAGER: 3,
  GUEST: 4,
} as const;

/**
 * 역할을 확정할 수 없을 때 쓰는 폴백.
 *
 * role_id/role_name을 따로 적으면 서로 어긋나기 쉬우므로(과거 role_id=2에
 * role_name='Guest'를 넣은 사례가 있었다) 항상 이 상수를 함께 사용한다.
 * 폴백은 반드시 최소 권한이어야 한다 — 0(SuperAdmin)으로 떨어지면
 * isAdminByRoleId()가 true가 되어 권한 상승이 된다.
 */
export const GUEST_ROLE = {
  role_id: ROLE_ID.GUEST,
  role_name: UserRole.GUEST,
} as const;

// role_name <-> role_id 양방향 매핑
export const ROLE_NAME_TO_ID: Record<string, number> = {
  [UserRole.SUPER_ADMIN]: ROLE_ID.SUPER_ADMIN,
  [UserRole.ADMIN]: ROLE_ID.ADMIN,
  [UserRole.COMPANY_MANAGER]: ROLE_ID.COMPANY_MANAGER,
  [UserRole.SITE_MANAGER]: ROLE_ID.SITE_MANAGER,
  [UserRole.GUEST]: ROLE_ID.GUEST,
};

// ── 계층 기반 권한 판단 (핵심 함수) ──

/** role_id가 requiredRoleId 이하이면 true (권한이 충분함) */
export const hasRoleAtLeast = (userRoleId: number, requiredRoleId: number): boolean =>
  userRoleId <= requiredRoleId;

// 기존 호환용 래퍼
export const isAdminByRoleId = (roleId?: number): boolean =>
  roleId !== undefined && hasRoleAtLeast(roleId, ROLE_ID.ADMIN);

export const isSuperAdminByRoleId = (roleId?: number): boolean => roleId === ROLE_ID.SUPER_ADMIN;

/** persist/API 불일치 시 role_name으로 role_id 보정 */
export function resolveRoleId(user: {
  role_id?: number | null;
  role_name?: string | null;
}): number | undefined {
  if (user.role_id !== undefined && user.role_id !== null) {
    return Number(user.role_id);
  }
  const name = user.role_name;
  if (name && name in ROLE_NAME_TO_ID) {
    return ROLE_NAME_TO_ID[name];
  }
  return undefined;
}

export function isAdminUser(
  user: { role_id?: number | null; role_name?: string | null } | null | undefined
): boolean {
  const roleId = user ? resolveRoleId(user) : undefined;
  return roleId !== undefined && isAdminByRoleId(roleId);
}

export function isCompanyManagerUser(
  user: { role_id?: number | null; role_name?: string | null } | null | undefined
): boolean {
  const roleId = user ? resolveRoleId(user) : undefined;
  return roleId !== undefined && isCompanyManagerByRoleId(roleId);
}

export function isSiteManagerUser(
  user: { role_id?: number | null; role_name?: string | null } | null | undefined
): boolean {
  const roleId = user ? resolveRoleId(user) : undefined;
  return roleId !== undefined && isSiteManagerByRoleId(roleId);
}

export const isCompanyManagerByRoleId = (roleId?: number): boolean =>
  roleId !== undefined && hasRoleAtLeast(roleId, ROLE_ID.COMPANY_MANAGER);

export const isSiteManagerByRoleId = (roleId?: number): boolean =>
  roleId !== undefined && hasRoleAtLeast(roleId, ROLE_ID.SITE_MANAGER);

// role_name 기반 판단 (프론트 UI용)
export const isAdmin = (role: string): boolean =>
  role === UserRole.ADMIN || role === UserRole.SUPER_ADMIN;

export const isSuperAdmin = (role: string): boolean => role === UserRole.SUPER_ADMIN;

// ── 권한 정의 (프론트 메뉴/UI용) ──

export interface Permission {
  canAccessMonitoring: boolean;
  canAccessDevice: boolean;
  canAccessConfigurator: boolean;

  canAccessAdmin: boolean;
  canAccessAdminAccounts: boolean;
  canAccessAllowedEmails: boolean;
  canAccessCompanies: boolean;
  canAccessSites: boolean;

  canManageUsers: boolean;
  canCreateUsers: boolean;
  canDeleteUsers: boolean;

  canManageCompanies: boolean;
  canManageSites: boolean;

  canManageSystemSettings: boolean;
  canViewSystemLogs: boolean;
}

const ROLE_PERMISSIONS: Record<UserRole, Permission> = {
  [UserRole.GUEST]: {
    canAccessMonitoring: false,
    canAccessDevice: false,
    canAccessConfigurator: false,
    canAccessAdmin: false,
    canAccessAdminAccounts: false,
    canAccessAllowedEmails: false,
    canAccessCompanies: false,
    canAccessSites: false,
    canManageUsers: false,
    canCreateUsers: false,
    canDeleteUsers: false,
    canManageCompanies: false,
    canManageSites: false,
    canManageSystemSettings: false,
    canViewSystemLogs: false,
  },
  [UserRole.SITE_MANAGER]: {
    canAccessMonitoring: true,
    canAccessDevice: true,
    canAccessConfigurator: true,
    canAccessAdmin: false,
    canAccessAdminAccounts: false,
    canAccessAllowedEmails: false,
    canAccessCompanies: false,
    canAccessSites: true,
    canManageUsers: false,
    canCreateUsers: false,
    canDeleteUsers: false,
    canManageCompanies: false,
    canManageSites: false,
    canManageSystemSettings: false,
    canViewSystemLogs: false,
  },
  [UserRole.COMPANY_MANAGER]: {
    canAccessMonitoring: true,
    canAccessDevice: true,
    canAccessConfigurator: true,
    canAccessAdmin: false,
    canAccessAdminAccounts: false,
    canAccessAllowedEmails: false,
    canAccessCompanies: true,
    canAccessSites: true,
    canManageUsers: true,
    canCreateUsers: true,
    canDeleteUsers: false,
    canManageCompanies: true,
    canManageSites: true,
    canManageSystemSettings: false,
    canViewSystemLogs: false,
  },
  [UserRole.ADMIN]: {
    canAccessMonitoring: true,
    canAccessDevice: true,
    canAccessConfigurator: true,
    canAccessAdmin: true,
    canAccessAdminAccounts: true,
    canAccessAllowedEmails: true,
    canAccessCompanies: true,
    canAccessSites: true,
    canManageUsers: true,
    canCreateUsers: true,
    canDeleteUsers: true,
    canManageCompanies: true,
    canManageSites: true,
    canManageSystemSettings: false,
    canViewSystemLogs: false,
  },
  [UserRole.SUPER_ADMIN]: {
    canAccessMonitoring: true,
    canAccessDevice: true,
    canAccessConfigurator: true,
    canAccessAdmin: true,
    canAccessAdminAccounts: true,
    canAccessAllowedEmails: true,
    canAccessCompanies: true,
    canAccessSites: true,
    canManageUsers: true,
    canCreateUsers: true,
    canDeleteUsers: true,
    canManageCompanies: true,
    canManageSites: true,
    canManageSystemSettings: true,
    canViewSystemLogs: true,
  },
};

export const getPermissions = (role: string): Permission =>
  ROLE_PERMISSIONS[role as UserRole] || ROLE_PERMISSIONS[UserRole.GUEST];
